import React, { useState, useEffect } from "react";
import SEO from "../seo";
const ReviewShippingPolicies = () => {
  const [activeSection, setActiveSection] = useState("shipping-rules");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["shipping-rules", "policy-pages", "tip"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Review Shipping & Policies – xPage Help Center"
        description="Learn how to set up shipping rules and legal policies for your xPage store. Ensure transparency and protect your business with clear policies."
        keywords="xPage shipping setup, store policies, return policy, refund policy, privacy policy, terms and conditions, ecommerce legal"
        canonical="https://help.xpage.ai/help/quick-launch-checklist/Review%20Shipping%20%26%20Policies"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Review Shipping & Policies
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Before launching your xPage landing page, it's essential to define
          your shipping methods and legal policies. This ensures transparency
          for your customers and helps prevent disputes post-purchase.
        </p>

        {/* Set Shipping Rules Section */}
        <section id="shipping-rules" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Set your shipping rules
          </h2>

          <p className="text-sm text-systemLabel mt-2 mb-4">
            xPage allows you to configure shipping zones and methods based on
            where you deliver. You can set flat rates, free shipping thresholds,
            or location-specific fees.
          </p>

          <p className="text-sm text-systemLabel mb-4">To set up:</p>

          <ol className="list-decimal list-inside space-y-1 mb-4">
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">
                Go to Settings &gt; Shipping
              </span>{" "}
              in your dashboard.
            </li>
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">Add your shipping zones</span>{" "}
              (e.g., US, Europe, Asia).
            </li>
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">Assign methods and rates</span>{" "}
              per zone (e.g., Standard, Express).
            </li>
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">
                Optionally, set delivery time estimates
              </span>{" "}
              per method (e.g., 5–7 days).
            </li>
          </ol>
        </section>

        {/* Customize Policy Pages Section */}
        <section id="policy-pages" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Customize your policy pages
          </h2>

          <p className="text-sm text-systemLabel mt-2 mb-4">
            Transparent policies build trust with shoppers. xPage makes it easy
            to add or edit:
          </p>

          <ul className="list-disc list-inside space-y-2 mb-4">
            <li className="text-sm text-systemLabel">
              <span className="font-semibold text-systemTitle">
                Shipping Policy:
              </span>{" "}
              Explain your fulfillment time, carriers, and delivery estimates.
            </li>
            <li className="text-sm text-systemLabel">
              <span className="font-semibold text-systemTitle">
                Return & Refund Policy:
              </span>{" "}
              Outline return eligibility, timeframes, and processing steps.
            </li>
            <li className="text-sm text-systemLabel">
              <span className="font-semibold text-systemTitle">
                Privacy Policy:
              </span>{" "}
              Inform customers how you handle their data and ensure compliance.
            </li>
            <li className="text-sm text-systemLabel">
              <span className="font-semibold text-systemTitle">
                Terms & Conditions:
              </span>{" "}
              Define store rules, disclaimers, and legal responsibilities.
            </li>
          </ul>

          <p className="text-sm text-systemLabel mb-4">To edit policies:</p>

          <ol className="list-decimal list-inside space-y-1 mb-4">
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">
                Go to Settings &gt; Legal & Policies.
              </span>
            </li>
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">Use the built-in templates</span>{" "}
              or write your own only Built in.
            </li>
            <li className="text-sm text-systemLabel mb-2">
              <span className="font-semibold">
                Preview the pages and publish
              </span>{" "}
              with one click.
            </li>
          </ol>

          <p className="text-sm text-systemLabel">
            These policies automatically appear in your store footer and during
            checkout.
          </p>
        </section>

        {/* Tip Section */}
        <section id="tip" className="scroll-mt-20">
          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4">
            <h3 className="text-lg font-semibold text-systemTitle mb-2">Tip</h3>
            <p className="text-sm text-systemLabel">
              Having clear shipping and legal information protects your store
              from disputes and builds confidence with buyers, especially
              first-time visitors. xPage ensures your policy setup is seamless,
              and editable at any time from your admin.
            </p>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#shipping-rules"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "shipping-rules"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Shipping Rules
              </a>
            </li>
            <li>
              <a
                href="#policy-pages"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "policy-pages"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Policy Pages
              </a>
            </li>
            <li>
              <a
                href="#tip"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "tip"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Tip
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ReviewShippingPolicies;
